import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { format, addDays } from 'date-fns';
import get from 'lodash.get';
import {
  Button,
  Box,
  MuiBox,
  Switch,
  Typography,
  FlexBox,
  InputField,
  Grid,
  SelectDropdown
} from '@kis-technologies/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import { useActions } from 'hooks/useActions';
import { actions as clientActions, selectors as clientsSelectors, Client, SalesReportSettings } from 'store/clients';
import { actions as configActions, selectors as configSelectors, Configuration } from 'store/configurations';
import { actions as settingsActions, selectors as settingsSelectors, KioskSales } from 'store/settings';
import { exportKioskSalesReport } from 'utils/sftKioskSalesReport';
import { getSortedList } from 'utils/stringFormat';
import { useStyles } from './styled';

const SettingsView: React.FC = () => {
  const classes = useStyles();

  // ACTIONS AND SELECTORS
  const {
    resetClientAction,
    invalidateCache,
    getConfigurations,
    updateConfiguration,
    resetConfigurationsAction,
    getSalesByKiosk,
    setConfigurationId,
    resetSettingsAction,
    resetSalesByKiosk
  } = useActions({
    resetClientAction: clientActions.resetAction,
    invalidateCache: clientActions.invalidateCache,
    getConfigurations: configActions.getConfigurations,
    updateConfiguration: configActions.updateConfiguration,
    resetConfigurationsAction: configActions.resetAction,
    getSalesByKiosk: settingsActions.getSalesByKiosk,
    setConfigurationId: settingsActions.setConfigurationId,
    resetSettingsAction: settingsActions.resetAction,
    resetSalesByKiosk: settingsActions.resetSalesByKiosk
  });
  const client = useSelector(clientsSelectors.selectClient) as Client;
  const clientAction = useSelector(clientsSelectors.selectAction);
  const configurations = useSelector(configSelectors.selectConfigurations);
  const configurationId = useSelector(settingsSelectors.selectConfigurationId);
  const configurationsAction = useSelector(configSelectors.selectAction);
  const salesByKiosk = useSelector(settingsSelectors.selectSalesByKiosk);
  const settingsAction = useSelector(settingsSelectors.selectAction);

  const [salesReportDate, setSalesReportDate] = useState<string>(format(new Date(), 'MM-dd-yyyy'));
  const [isSalesReportDateValid, setIsSalesReportDateValid] = useState<boolean>(true);
  const [salesReportStatus, setSalesReportStatus] = useState<string>('');
  const [salesReportDayOfEvent, setSalesReportDayOfEvent] = useState<number>(0);
  const [salesReportCurrentYearDate, setSalesReportCurrentYearDate] = useState<string>('');
  const [salesReportPreviousYearDate, setSalesReportPreviousYearDate] = useState<string>('');
  const [oneDayCurrentYearSalesByKiosk, setOneDayCurrentYearSalesByKiosk] = useState<KioskSales[] | undefined>();
  const [currentYearSalesByKiosk, setCurrentYearSalesByKiosk] = useState<KioskSales[] | undefined>();
  const [oneDayPreviousYearSalesByKiosk, setOneDayPreviousYearSalesByKiosk] = useState<KioskSales[] | undefined>();
  const [previousYearSalesByKiosk, setPreviousYearSalesByKiosk] = useState<KioskSales[] | undefined>();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [customerLookupValue, setCustomerLookupValue] = useState<boolean>(false);
  const [customerCreationValue, setCreationValue] = useState<boolean>(false);
  const [surveyPageValue, setSurveyPageValue] = useState<boolean>(false);
  const [donationPopupValue, setDonationPopupValue] = useState<boolean>(false);
  const [selectedConfiguration, setSelectedConfiguration] = useState<Configuration | null>(null);
  const [initialSettings, setInitialSettings] = useState({
    lookup: false,
    creation: false,
    surveyPage: false,
    donationPopup: false
  });
  const [hasChanges, setHasChanges] = useState(false);

  const salesReportSettings: SalesReportSettings = get(client, 'configuration.salesReportSettings', {});
  const ticketing = get(selectedConfiguration, 'ticketing');
  const customerIdentification = get(ticketing, 'customerIdentification');
  const customerLookup = get(customerIdentification, 'lookup');
  const customerCreation = get(customerIdentification, 'creation');
  const survey = get(ticketing, 'survey');
  const donationOverlay = get(ticketing, 'donations.overlay');

  useEffect(() => {
    getConfigurations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  useEffect(() => {
    if (configurations.length && !configurationId) {
      setConfigurationId(configurations[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurations]);

  useEffect(() => {
    let configuration = null;
    if (configurationId && configurations.length) {
      configuration = configurations.find((c) => c.id === configurationId);
      if (!configuration) configuration = configurations[0];
    }
    setSelectedConfiguration(configuration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurationId, configurations]);

  useEffect(() => {
    if (selectedConfiguration) {
      const lookup = customerLookup?.enabled ?? false;
      const creation = customerCreation?.enabled ?? false;
      const surveyPage = survey?.enabled ?? false;
      const donationPopup = donationOverlay?.enabled ?? false;

      setCustomerLookupValue(lookup);
      setCreationValue(creation);
      setSurveyPageValue(surveyPage);
      setDonationPopupValue(donationPopup);

      setInitialSettings({
        lookup,
        creation,
        surveyPage,
        donationPopup
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConfiguration]);

  useEffect(() => {
    const haveValuesChanged =
      customerLookupValue !== initialSettings.lookup ||
      customerCreationValue !== initialSettings.creation ||
      surveyPageValue !== initialSettings.surveyPage ||
      donationPopupValue !== initialSettings.donationPopup;

    setHasChanges(haveValuesChanged);
  }, [customerLookupValue, customerCreationValue, surveyPageValue, donationPopupValue]);

  useEffect(() => {
    if (configurationsAction.success) {
      setHasChanges(false);
      resetConfigurationsAction();
    }
  }, [configurationsAction.success]);

  /**
   * This triggers actions to build the kiosk sales report for SFT.
   *
   * setTimeout() is used to ensure that the state updates are
   * completed before the next action is triggered.
   */
  useEffect(() => {
    if (salesByKiosk && salesReportStatus === 'oneDayCurrentYear') {
      // Save the response for the 1st date range (out of 4)
      setOneDayCurrentYearSalesByKiosk(salesByKiosk);
      setSalesReportStatus('oneDayPreviousYear');
      resetSalesByKiosk();
      // Get sales for the 2nd date range (out of 4)
      setTimeout(() => {
        getSalesByKiosk({
          startDate: salesReportPreviousYearDate,
          endDate: salesReportPreviousYearDate
        });
      }, 0);
    } else if (salesByKiosk && salesReportStatus === 'oneDayPreviousYear') {
      // Save the response for the 2nd date range (out of 4)
      setOneDayPreviousYearSalesByKiosk(salesByKiosk);
      setSalesReportStatus('currentYear');
      resetSalesByKiosk();
      // Get sales for the 3rd date range (out of 4)
      setTimeout(() => {
        getSalesByKiosk({
          startDate: salesReportSettings.currentEventStartDate,
          endDate: salesReportCurrentYearDate
        });
      }, 0);
    } else if (salesByKiosk && salesReportStatus === 'currentYear') {
      // Save the response for the 3rd date range (out of 4)
      setCurrentYearSalesByKiosk(salesByKiosk);
      setSalesReportStatus('previousYear');
      resetSalesByKiosk();
      // Get sales for the 4th date range (out of 4)
      setTimeout(() => {
        getSalesByKiosk({
          startDate: salesReportSettings.previousEventStartDate,
          endDate: salesReportPreviousYearDate
        });
      }, 0);
    } else if (salesByKiosk && salesReportStatus === 'previousYear') {
      // Save the response for the 4th date range (out of 4)
      setPreviousYearSalesByKiosk(salesByKiosk);
      setSalesReportStatus('exportData');
      setTimeout(() => resetSalesByKiosk(), 0);
    } else if (salesReportStatus === 'exportData') {
      setSalesReportStatus('');
      // Generate the xls file and download
      exportKioskSalesReport({
        salesReportSettings,
        salesReportCurrentYearDate,
        salesReportPreviousYearDate,
        salesReportDayOfEvent,
        oneDayCurrentYearSalesByKiosk,
        oneDayPreviousYearSalesByKiosk,
        currentYearSalesByKiosk,
        previousYearSalesByKiosk
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesByKiosk]);

  // Reset the sales report status if there is an error
  useEffect(() => {
    if (settingsAction.error) setSalesReportStatus('');
  }, [settingsAction.error]);

  // Validate and update the sales report date when the input value changes
  const onSalesReportDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const value = e.target.value.trim();

    // Validate the format (MM-DD-YYYY)
    const dateRegex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-(\d{4})$/;
    const isValidDate = dateRegex.test(value);

    // Set the input value and the valid status that enables/disables the button
    setSalesReportDate(value);
    setIsSalesReportDateValid(isValidDate);
  };

  // Start generating the sales report, triggering a useEffect() to complete it
  const onBuildReportClick = () => {
    const { currentEventStartDate, previousEventStartDate } = salesReportSettings;

    try {
      // Reformat the input date
      const currentYearReportDate = format(new Date(salesReportDate), 'yyyy-MM-dd');
      setSalesReportCurrentYearDate(currentYearReportDate);

      // Get the difference (in days) from the event start date that is configured
      const date1 = new Date(`${currentEventStartDate}T00:00:00`);
      const date2 = new Date(`${currentYearReportDate}T00:00:00`);
      const diffInTime = date2.getTime() - date1.getTime();
      const diffInDays = Math.round(diffInTime / (1000 * 3600 * 24));

      if (diffInDays < 0) {
        setErrorMessage(`${salesReportDate} is not a valid date for this report`);
      } else {
        // Use the difference to determine the date of the previous year
        const previousYearSameDayOfEvent = addDays(new Date(`${previousEventStartDate}T00:00:00`), diffInDays);
        setSalesReportPreviousYearDate(format(previousYearSameDayOfEvent, 'yyyy-MM-dd'));
        setSalesReportDayOfEvent(diffInDays + 1);

        // Get sales for the 1st date range (out of 4)
        setSalesReportStatus('oneDayCurrentYear');
        setTimeout(() => {
          getSalesByKiosk({
            startDate: currentYearReportDate,
            endDate: currentYearReportDate
          });
        }, 0);
      }
    } catch (err) {
      setErrorMessage('Unable to determine the date range for the report');
    }
  };

  const onErrorOverlayClick = () => {
    if (clientAction.error) resetClientAction();
    if (settingsAction.error) resetSettingsAction();
    if (errorMessage) setErrorMessage('');
  };

  const onResetSettings = () => {
    setCustomerLookupValue(initialSettings.lookup);
    setCreationValue(initialSettings.creation);
    setSurveyPageValue(initialSettings.surveyPage);
    setDonationPopupValue(initialSettings.donationPopup);
    setHasChanges(false);
  };

  const onSaveSettings = () => {
    const updatedDonationOverlay = donationOverlay ? { ...donationOverlay, enabled: donationPopupValue } : undefined;
    const updatedSurvey = survey ? { ...survey, enabled: surveyPageValue } : undefined;
    const updatedCustomerLookup = customerLookup ? { ...customerLookup, enabled: customerLookupValue } : undefined;
    const updatedCustomerCreation = customerCreation
      ? { ...customerCreation, enabled: customerCreationValue }
      : undefined;

    updateConfiguration({
      id: (selectedConfiguration as Configuration).id,
      ticketing: {
        ...ticketing,
        donations: {
          ...get(ticketing, 'donations', {}),
          overlay: updatedDonationOverlay
        },
        survey: updatedSurvey,
        customerIdentification: {
          ...customerIdentification,
          lookup: updatedCustomerLookup,
          creation: updatedCustomerCreation
        }
      }
    });
  };

  return (
    <>
      {(clientAction.error || settingsAction.error || errorMessage) && (
        <ActionOverlay
          title={
            <Typography className={classes.errorOverlayHeader} sx={{ lineHeight: 1, fontWeight: 600 }}>
              {clientAction.error || settingsAction.error || errorMessage}
            </Typography>
          }
          confirmText="Ok"
          onConfirm={onErrorOverlayClick}
          width={800}
          height={350}
        />
      )}
      <>
        {/* Page header */}
        <Grid className={classes.settingsHeading}>
          <Box flexOverride="none">
            <Typography className={classes.settingsHeaderText}>Settings</Typography>
          </Box>
        </Grid>
        {/* Clear cache button */}
        <FlexBox align="center" fullWidth sx={{ margin: '0px 40px 48px 40px' }}>
          <Typography sx={{ margin: '0 20px 0 0', fontSize: '18px' }}>Clear cached event data</Typography>
          <Button customSize="small" isLoading={clientAction.pending} onClick={invalidateCache}>
            Clear Cache
          </Button>
        </FlexBox>
        {/* Kiosk Sales Report (SFT only) */}
        {client.configuration.salesReportSettings && (
          <FlexBox direction="column" fullWidth sx={{ margin: '0px 40px 48px 40px' }}>
            <Typography fontSize={24} fontWeight={700}>
              Kiosk Sales Report
            </Typography>
            <FlexBox sx={{ margin: '24px 0 0' }}>
              <MuiBox>
                <InputField
                  label="Date (MM-DD-YYYY)"
                  size="medium"
                  placeholder="MM-DD-YYYY"
                  value={salesReportDate}
                  sx={{ height: '45px' }}
                  onChange={(e) => onSalesReportDateInputChange(e)}
                  data-testid="kiosk-sales-report-date-input"
                />
              </MuiBox>
              <Button
                customSize="small"
                disabled={!isSalesReportDateValid}
                isLoading={settingsAction.pending || salesReportStatus !== ''}
                onClick={onBuildReportClick}
                className={classes.salesReportButton}
                data-testid="build-report-button"
              >
                Build Report
              </Button>
            </FlexBox>
          </FlexBox>
        )}
        {/* Configuration selection */}
        <FlexBox fullWidth align="center" sx={{ padding: '0px 40px 0px 40px' }}>
          <Typography fontSize={24} fontWeight={700}>
            Configuration
          </Typography>
          <Box width={20} />
          <SelectDropdown
            items={getSortedList(configurations, 'name')}
            itemToString={(item: Configuration) => item && item.name}
            selectedItem={selectedConfiguration as Configuration}
            onChange={(item: Configuration) => setConfigurationId(item.id)}
            data-testid="configuration-selector"
          />
        </FlexBox>
        {/* Container of settings */}
        <Box
          raised
          height="625px"
          minHeight="625px"
          flexOverride="none"
          padding="0px 24px"
          margin="24px 40px"
          sx={{ borderRadius: '4px', overflowY: 'auto' }}
        >
          <FlexBox align="center" justify="between" className={classes.contentBox}>
            <Typography className={classes.configSettingLabel} sx={{ color: !customerLookup ? 'grey' : 'black' }}>
              Customer Lookup
            </Typography>
            <Switch
              checked={customerLookupValue === true}
              checkedLabel="Enabled"
              uncheckedLabel="Disabled"
              disabled={!customerLookup}
              containerStyle={{ width: '130px' }}
              onChange={(e) => setCustomerLookupValue(e.target.checked)}
              data-testid="customer-lookup-switch"
            />
          </FlexBox>
          <FlexBox align="center" justify="between" className={classes.contentBox}>
            <Typography className={classes.configSettingLabel} sx={{ color: !customerCreation ? 'grey' : 'black' }}>
              Customer Creation
            </Typography>
            <Switch
              checked={customerCreationValue === true}
              checkedLabel="Enabled"
              uncheckedLabel="Disabled"
              disabled={!customerCreation}
              containerStyle={{ width: '130px' }}
              onChange={(e) => setCreationValue(e.target.checked)}
              data-testid="customer-creation-switch"
            />
          </FlexBox>
          <FlexBox align="center" justify="between" className={classes.contentBox}>
            <Typography className={classes.configSettingLabel} sx={{ color: !survey ? 'grey' : 'black' }}>
              Survey Page
            </Typography>
            <Switch
              checked={surveyPageValue === true}
              checkedLabel="Enabled"
              disabled={!survey}
              uncheckedLabel="Disabled"
              containerStyle={{ width: '130px' }}
              onChange={(e) => setSurveyPageValue(e.target.checked)}
              data-testid="survey-switch"
            />
          </FlexBox>
          <FlexBox align="center" justify="between" className={classes.contentBox}>
            <Typography className={classes.configSettingLabel} sx={{ color: !donationOverlay ? 'grey' : 'black' }}>
              Donation Pop-up
            </Typography>
            <Switch
              checked={donationPopupValue === true}
              checkedLabel="Enabled"
              disabled={!donationOverlay}
              uncheckedLabel="Disabled"
              containerStyle={{ width: '130px' }}
              onChange={(e) => setDonationPopupValue(e.target.checked)}
              data-testid="donation-roundup-switch"
            />
          </FlexBox>
        </Box>
        <FlexBox justify="end" sx={{ margin: '0px 0px 24px', padding: '0px 40px', width: '100%' }}>
          <Button
            onClick={onResetSettings}
            sx={{ marginRight: '20px' }}
            customSize="small"
            width={144}
            disabled={!hasChanges}
          >
            Reset
          </Button>
          <Button customSize="small" width={144} disabled={!hasChanges} onClick={onSaveSettings}>
            Save
          </Button>
        </FlexBox>
      </>
    </>
  );
};

export default SettingsView;
